import type { CalcReport, CalculatorInfo, CalcRunBody, ChatMessageView, DailyCardView, Goal, LifeStatus, Priority, ReportBlock, GiftsView, HistoryItem, HistoryView, MenuView, Product, ReadingView, ReferralView, UserView } from "@psy/shared";
import { getInitData } from "./platform";

export type { CalcReport, CalculatorInfo, CalcRunBody, ChatMessageView, Goal, LifeStatus, Priority, ReportBlock, GiftsView, HistoryItem, HistoryView, MenuView, Product, ReadingView, ReferralView, UserView };
export type MenuModule = MenuView["modules"][number];
export type Relation = "romantic" | "friend" | "business";

export interface ReadingResponse {
  reading: ReadingView;
  streak: number;
  coinsAwarded: number;
}

export interface AppConfig {
  brand: string;
  botUsername: string;
  termsUrl?: string;
  supportUrl?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /** Текст от сервера (например, сообщение о техобслуживании). */
    readonly text?: string,
  ) {
    super(code);
  }
}

async function callOnce<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/app/api${path}`, {
      method,
      headers: {
        authorization: `tma ${getInitData()}`,
        ...(method === "POST" ? { "content-type": "application/json" } : {}),
      },
      ...(method === "POST" ? { body: JSON.stringify(body ?? {}) } : {}),
    });
  } catch {
    throw new ApiError(0, "network");
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string } | null)?.error ?? `http_${res.status}`, (data as { text?: string } | null)?.text);
  return data as T;
}

/** Повторяемая ошибка: нет сети или сервис временно недоступен (503). */
const transient = (e: unknown) => e instanceof ApiError && (e.code === "network" || (e.status === 503 && e.code !== "maintenance"));
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** GET безопасно повторяем сам (до 2 раз с паузой); POST — никогда, чтобы не задвоить действие (оплата, избранное). */
async function call<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  const delays = method === "GET" ? [600, 1500] : [];
  for (let i = 0; ; i++) {
    try {
      return await callOnce<T>(method, path, body);
    } catch (e) {
      const wait = delays[i];
      if (wait === undefined || !transient(e)) throw e;
      await sleep(wait);
    }
  }
}

export type Query = Record<string, string | number | undefined>;

function qs(q: Query): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined) p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const api = {
  config: () => call<AppConfig>("GET", "/config"),
  me: () => call<MenuView>("GET", "/me"),
  setBirth: (
    birthDate: string,
    timezone?: string,
    extra?: { lifeStatus?: LifeStatus; priority?: Priority; goal?: Goal; referralCode?: string },
  ) => call<{ user: UserView; trial: { started: boolean } }>("POST", "/birth", { birthDate, ...(timezone ? { timezone } : {}), ...extra }),
  reading: (moduleId: string, query: Query = {}) => call<ReadingResponse>("GET", `/readings/${encodeURIComponent(moduleId)}${qs(query)}`),
  dailyCard: () => call<{ card: DailyCardView }>("GET", "/daily-card"),
  setDailyMood: (mood: "good" | "meh" | "hard") => call<{ ok: true }>("POST", "/daily-card/mood", { mood }),
  chatHistory: () => call<{ items: ChatMessageView[] }>("GET", "/chat"),
  askChat: (question: string) => call<{ text: string; audioBase64: string | null }>("POST", "/chat", { question }),
  expandReading: (moduleId: string, query: Query = {}) =>
    call<{ title: string; text: string; cached: boolean }>("GET", `/readings/${encodeURIComponent(moduleId)}/expand${qs(query)}`),
  aiReport: (moduleId: string) =>
    call<
      | { title: string; status: "running"; sections: { id: string; heading: string; body: string }[] }
      | { title: string; status: "error" }
      | { title: string; status: "done"; sections: { id: string; heading: string; body: string }[]; provider: string }
    >("GET", `/readings/${encodeURIComponent(moduleId)}/ai-report`),
  readingWith: (moduleId: string, input: { otherBirthDate: string; relation?: Relation }) =>
    call<ReadingResponse>("POST", `/readings/${encodeURIComponent(moduleId)}`, input),
  history: () => call<HistoryView>("GET", "/history"),
  referral: () => call<ReferralView>("GET", "/referral"),
  gifts: () => call<GiftsView>("GET", "/gifts"),
  claimGift: (giftId: string) => call<{ ok: true }>("POST", `/gifts/${encodeURIComponent(giftId)}/claim`, {}),
  palmHistory: () => call<{ items: { lines: { title: string; body: string }[]; createdAt: string }[] }>("GET", "/palm"),
  readPalm: (photoBase64: string, mediaType: string, handSide: "right" | "left", isLeadingHand: boolean) =>
    call<{ lines: { title: string; body: string }[]; cached: boolean }>("POST", "/palm", { photoBase64, mediaType, handSide, isLeadingHand }),
  setFavorite: (moduleId: string, on: boolean) => call<HistoryView>("POST", "/favorites", { moduleId, on }),
  catalog: () => call<{ products: Product[] }>("GET", "/catalog"),
  invoice: (productKey: string) => call<{ url: string }>("POST", "/invoice", { productKey }),
  settings: (input: { dailyPush?: boolean; lifeStatus?: LifeStatus; priority?: Priority; goal?: Goal; birthTime?: string; birthCity?: string }) =>
    call<{ user: UserView }>("POST", "/settings", input),
  calculators: () => call<{ calculators: CalculatorInfo[] }>("GET", "/calculators"),
  runCalculator: (id: string, body: CalcRunBody) => call<CalcReport>("POST", `/calculators/${encodeURIComponent(id)}`, body),
  /** Одноразовая https-ссылка на тот же PDF — для Telegram.WebApp.openLink (см. platform.ts). */
  pdfReadingLink: (moduleId: string) => call<{ url: string }>("GET", `/readings/${encodeURIComponent(moduleId)}/pdf-link`),
  aiPdfLink: (moduleId: string) =>
    call<{ url: string } | { status: "running" | "error" }>("GET", `/readings/${encodeURIComponent(moduleId)}/ai-pdf-link`),
};

/** Дата ГГГГ-ММ-ДД → ДД.ММ.ГГГГ. */
export function formatBirth(iso: string | null): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/u.exec(iso) : null;
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
}

/** Дата рождения «4 сентября 1987», для карточки «Обо мне» (ISO ГГГГ-ММ-ДД). */
export function formatBirthLong(iso: string | null): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/u.exec(iso) : null;
  if (!m) return "";
  const months = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
  return `${Number(m[3])} ${months[Number(m[2]) - 1]} ${m[1]}`;
}

// ︎ — селектор текстового (а не эмодзи-цветного) начертания: без него Android/некоторые шрифты
// рисуют эти символы как цветные эмодзи-иконки, что не подходит под золотисто-тёмный стиль приложения.
const ZODIAC: readonly { name: string; symbol: string; from: [number, number]; to: [number, number] }[] = [
  { name: "Козерог", symbol: "♑︎", from: [12, 22], to: [1, 19] },
  { name: "Водолей", symbol: "♒︎", from: [1, 20], to: [2, 18] },
  { name: "Рыбы", symbol: "♓︎", from: [2, 19], to: [3, 20] },
  { name: "Овен", symbol: "♈︎", from: [3, 21], to: [4, 19] },
  { name: "Телец", symbol: "♉︎", from: [4, 20], to: [5, 20] },
  { name: "Близнецы", symbol: "♊︎", from: [5, 21], to: [6, 20] },
  { name: "Рак", symbol: "♋︎", from: [6, 21], to: [7, 22] },
  { name: "Лев", symbol: "♌︎", from: [7, 23], to: [8, 22] },
  { name: "Дева", symbol: "♍︎", from: [8, 23], to: [9, 22] },
  { name: "Весы", symbol: "♎︎", from: [9, 23], to: [10, 22] },
  { name: "Скорпион", symbol: "♏︎", from: [10, 23], to: [11, 21] },
  { name: "Стрелец", symbol: "♐︎", from: [11, 22], to: [12, 21] },
];

/** Знак зодиака по классическим календарным границам (не по эфемеридам — для карточки профиля этого достаточно). */
export function zodiacSign(iso: string | null): { name: string; symbol: string } | null {
  const m = iso ? /^\d{4}-(\d{2})-(\d{2})/u.exec(iso) : null;
  if (!m) return null;
  const month = Number(m[1]);
  const day = Number(m[2]);
  for (const z of ZODIAC) {
    const [fm, fd] = z.from;
    const [tm, td] = z.to;
    if (fm === tm ? month === fm && day >= fd && day <= td : (month === fm && day >= fd) || (month === tm && day <= td)) return { name: z.name, symbol: z.symbol };
  }
  return null;
}
